import { useState, useEffect } from 'react';
import {
  Modal,
  TextInput,
  Select,
  SelectItem,
  InlineNotification,
} from '@carbon/react';
import { Node, Edge } from '@xyflow/react';
import { ConceptNodeData } from './ConceptNode';
import {
  convertToJsonLd,
  serializeJsonLd,
  ExportNode,
  ExportEdge,
} from '@snxt/snxt-onto-share';

export type ExportFormat = 'jsonld';

interface ExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  nodes: Node[];
  edges: Edge[];
  ontologyName?: string;
}

/**
 * Trigger a file download in the browser.
 */
function downloadFile(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * ExportDialog Component
 *
 * Modal dialog for exporting the current ontology graph in various formats.
 * Currently supports JSON-LD format for ingestion in external tools.
 */
export const ExportDialog = ({
  isOpen,
  onClose,
  nodes,
  edges,
  ontologyName = 'ontology',
}: ExportDialogProps) => {
  const [format, setFormat] = useState<ExportFormat>('jsonld');
  const [namespace, setNamespace] = useState('http://example.org/ontology#');
  const [prefix, setPrefix] = useState('onto');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setError(null);
      setSuccessMessage(null);
    }
  }, [isOpen]);

  const handleExport = () => {
    setError(null);
    setSuccessMessage(null);

    try {
      if (nodes.length === 0) {
        setError('No concepts to export. Add some concepts to your ontology first.');
        return;
      }

      // Validate namespace
      if (!namespace.trim()) {
        setError('Namespace URI is required');
        return;
      }

      // Validate prefix
      if (!prefix.trim()) {
        setError('Namespace prefix is required');
        return;
      }

      // Convert ReactFlow nodes/edges to export format
      const exportNodes: ExportNode[] = nodes.map((node) => {
        const data = node.data as ConceptNodeData;
        return {
          id: node.id,
          label: data.label,
          definition: data.definition || undefined,
          properties: data.properties || [],
        };
      });

      const exportEdges: ExportEdge[] = edges.map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        label: typeof edge.label === 'string' ? edge.label : undefined,
      }));

      if (format === 'jsonld') {
        const jsonLdDoc = convertToJsonLd(exportNodes, exportEdges, {
          namespace: namespace.trim(),
          prefix: prefix.trim(),
          name: ontologyName,
        });

        const content = serializeJsonLd(jsonLdDoc);
        const filename = `${ontologyName.replace(/[^a-zA-Z0-9_-]/g, '_').toLowerCase()}.jsonld`;

        downloadFile(content, filename, 'application/ld+json');
        setSuccessMessage(`Exported successfully as ${filename}`);

        // Close after a brief delay to show success
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    }
  };

  return (
    <Modal
      open={isOpen}
      onRequestClose={onClose}
      onRequestSubmit={handleExport}
      modalHeading="Export Ontology"
      primaryButtonText="Export"
      secondaryButtonText="Cancel"
      primaryButtonDisabled={nodes.length === 0}
      size="sm"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <p style={{
          color: 'var(--cds-text-secondary)',
          fontSize: '0.875rem',
          marginBottom: '0.5rem',
        }}>
          Export your ontology for ingestion in external tools.
        </p>

        {/* Stats summary */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          padding: '0.75rem 1rem',
          backgroundColor: 'var(--cds-layer-02)',
          borderRadius: '4px',
          fontSize: '0.875rem',
          color: 'var(--cds-text-secondary)',
        }}>
          <span><strong>{nodes.length}</strong> concepts</span>
          <span>•</span>
          <span><strong>{edges.length}</strong> relationships</span>
        </div>

        <Select
          id="export-format"
          labelText="Format"
          value={format}
          onChange={(e) => setFormat(e.target.value as ExportFormat)}
        >
          <SelectItem value="jsonld" text="JSON-LD (.jsonld)" />
        </Select>

        <TextInput
          id="export-namespace"
          labelText="Namespace URI"
          placeholder="http://example.org/ontology#"
          value={namespace}
          onChange={(e) => setNamespace(e.target.value)}
          helperText="The base IRI for your ontology entities"
        />

        <TextInput
          id="export-prefix"
          labelText="Prefix"
          placeholder="onto"
          value={prefix}
          onChange={(e) => setPrefix(e.target.value)}
          helperText="Short prefix used in the JSON-LD context (e.g. onto, sro)"
        />

        {error && (
          <InlineNotification
            kind="error"
            title="Error"
            subtitle={error}
            lowContrast
            hideCloseButton
          />
        )}

        {successMessage && (
          <InlineNotification
            kind="success"
            title="Success"
            subtitle={successMessage}
            lowContrast
            hideCloseButton
          />
        )}
      </div>
    </Modal>
  );
};
